import React, { useState, useEffect } from 'react';
import { medicsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  User,
  Mail,
  Phone,
  Shield,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import NotificationPreferences from '../../components/portal/NotificationPreferences';

const STATUS_STYLES = {
  active: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  inactive: 'bg-red-500/15 text-red-400 border-red-500/20',
};

export default function MedicProfile() {
  const { user } = useAuth();
  const [medicProfile, setMedicProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await medicsAPI.getMyProfile();
      setMedicProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medic-profile-container">
      {/* Profile Header */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[#1ABC9C]/20 flex items-center justify-center">
              <User className="w-12 h-12 text-[#1ABC9C]" />
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-2xl font-bold text-[#F2F2F2] font-['Manrope']">
                {medicProfile?.name || user?.name}
              </h2>
              <p className="text-[#A1A1AA] mt-1">{medicProfile?.role || 'Medic'}</p>
              <div className="mt-2">
                <Badge className={STATUS_STYLES[medicProfile?.compliance_status || 'pending']}>
                  {medicProfile?.compliance_status || 'pending'}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] font-['Manrope']">Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                  <Mail className="w-5 h-5 text-[#1ABC9C]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Email</p>
                  <p className="text-[#F2F2F2]">{medicProfile?.email || user?.email}</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                  <Phone className="w-5 h-5 text-[#FFC04D]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Phone</p>
                  <p className="text-[#F2F2F2]">{medicProfile?.phone || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Information */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] font-['Manrope']">Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/15">
                  <Shield className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Role</p>
                  <p className="text-[#F2F2F2]">{medicProfile?.role || 'Medic'}</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-500/15">
                  <Calendar className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Member Since</p>
                  <p className="text-[#F2F2F2]">
                    {medicProfile?.created_at 
                      ? format(new Date(medicProfile.created_at), 'MMM dd, yyyy')
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Summary */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] font-['Manrope']">Documents Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['training', 'insurance', 'certification', 'other'].map(type => {
              const count = medicProfile?.documents?.filter(d => d.type === type).length || 0;
              return (
                <div key={type} className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 text-center">
                  <p className="text-2xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                    {count}
                  </p>
                  <p className="text-sm text-[#A1A1AA] capitalize">{type}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <NotificationPreferences />

      {/* Info Note */}
      <Card className="bg-[#1A1A1A] border-white/5">
        <CardContent className="p-4">
          <p className="text-sm text-[#A1A1AA]">
            To update your profile information or upload new documents, please contact your administrator.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
